import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, useLoad } from "@/lib/api";
import { Panel, Table, Health, money, day } from "@/pages/console/Kit";

const ACTIONS = [["note", "Add note"], ["suspend", "Suspend"], ["reactivate", "Reactivate"], ["extend_free_month", "Give a free month"], ["discount", "One-off discount"], ["cancel", "Cancel at renewal"], ["support_view", "View (with consent)"], ["delete_data", "Delete data"]];

function Detail({ id, onClose, onChanged }) {
  const api = useApi();
  const d = useLoad(id ? `/admin/customers/${id}` : null);
  const [action, setAction] = useState("note");
  const [reason, setReason] = useState("");
  const [percent, setPercent] = useState(20);
  const [view, setView] = useState(null);
  const run = async () => {
    try {
      const r = await api("post", `/admin/customers/${id}/action`, { action, reason, percent: Number(percent) });
      if (r.view) setView(r.view);
      toast.success("Done"); setReason(""); d.reload(); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  const x = d.data;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="customer-detail">
        <DialogHeader><DialogTitle>{x?.org.name || "Loading…"}</DialogTitle></DialogHeader>
        {x && <div className="space-y-4 text-sm">
          <p className="text-slate-600">Company {x.org.company_number || "-"} · {x.org.billing_email || "-"} · status <b>{x.org.sub_status || (x.org.exempt ? "house account" : "not started")}</b> · <Health h={x.health.health} /></p>
          <p className="text-slate-600">{x.health.invoices} invoices · {x.health.statements} statements · {x.health.queries_raised} queries · found {money(x.health.found)} · recovered {money(x.health.recovered)} · support access {x.support_consent ? "granted" : "not granted"}</p>
          <Table rows={x.branches} cols={[["name", "Branch"], ["postcode", "Postcode"], ["gphc", "GPhC"], ["gphc_checked", "Checked", (b) => (b.gphc_checked ? "Yes" : "No")]]} />
          <Table rows={x.users} cols={[["email", "User"], ["role", "Role"], ["last_seen", "Last seen", (u) => day(u.last_seen)]]} />
          {x.notes.length > 0 && <div><p className="font-semibold">Notes</p>{x.notes.map((n, i) => <p key={i} className="border-t border-slate-100 py-1.5">{n.text} <span className="text-xs text-slate-400">· {n.by} · {day(n.at)}</span></p>)}</div>}
          <div className="grid gap-2 rounded-2xl bg-slate-50 p-4 sm:grid-cols-[180px_1fr_auto]">
            <select value={action} onChange={(e) => setAction(e.target.value)} className="h-10 rounded-md border bg-white px-2" data-testid="customer-action-select">{ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <div className="flex gap-2"><Input placeholder="Reason (logged)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="customer-action-reason" />{action === "discount" && <Input type="number" className="w-20" value={percent} onChange={(e) => setPercent(e.target.value)} />}</div>
            <Button onClick={run} disabled={reason.length < 3} data-testid="customer-action-run">Run</Button>
          </div>
          {view && <p className="text-xs text-slate-500" data-testid="customer-support-view">Support view: {view.docs.length} recent documents, {view.queries.length} recent queries (logged).</p>}
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const list = useLoad(`/admin/customers?q=${encodeURIComponent(q)}`);
  const [open, setOpen] = useState(null);
  return (
    <Panel title="Customers" testid="console-customers" right={<Input className="w-64" placeholder="Search name, company no., email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search" />}>
      <Table testid="customers-table" rows={list.data || []} cols={[
        ["name", "Pharmacy", (r) => <button className="text-left font-semibold text-teal-700 hover:underline" onClick={() => setOpen(r.id)} data-testid={`customer-open-${r.id}`}>{r.name}</button>],
        ["status", "Status", (r) => (r.exempt ? "House (free)" : r.status || "not started")],
        ["access", "Access"], ["branches", "Branches"], ["mrr", "MRR", (r) => money(r.mrr)],
        ["health", "Health", (r) => <Health h={r.health} />], ["uploads_14d", "Uploads 14d"],
        ["gphc_unchecked", "GPhC to check"], ["created_at", "Joined", (r) => day(r.created_at)]]} />
      <Detail id={open} onClose={() => setOpen(null)} onChanged={list.reload} />
    </Panel>
  );
}
